import { useSyncExternalStore } from "react"

/**
 * Module-level store for "which chat is the user actively looking at, and is
 * the tab focused".
 *
 * Why module-level (not a React context):
 * - There is exactly one active chat at a time across the whole app (the /ai
 *   page and the floating side panel render the same `ChatWindow`), so a single
 *   global cell is the natural shape — no per-tree isolation needed.
 * - The writer (`ChatWindow`) and the readers (`useUnreadChatCount`,
 *   `useClearUnreadOnFocus`) live far apart in the tree; a context would force
 *   an awkward provider wrapping both. A `useSyncExternalStore`-backed module
 *   store lets any component subscribe without a shared ancestor — the same
 *   pattern the presence layer uses.
 *
 * The state drives the flicker fix: a reply landing on the chat you're viewing
 * must never flash an unread dot. Readers treat
 * `activeSessionId === session.id && isFocused` as "already read" at display
 * time, before the clear-unread push round-trips.
 *
 * SSR safety: only imported by client components; no Next.js server boundary
 * touches it.
 */

export interface ActiveChatSessionState {
  /** The session currently rendered by the visible ChatWindow, if any. */
  activeSessionId: string | null
  /**
   * Whether the tab is visible (foreground). Named `isFocused` for historical
   * reasons, but the writer (`ChatWindow`) gates on `visibilityState` alone, not
   * `document.hasFocus()` — a visible-but-keyboard-unfocused tab still counts as
   * the user looking at the chat.
   */
  isFocused: boolean
}

let state: ActiveChatSessionState = {
  activeSessionId: null,
  // Default to focused: a freshly-mounted app is in the foreground, and the
  // focus/visibility listeners reconcile the real value immediately.
  isFocused: true,
}

const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

export function setActiveSessionId(sessionId: string | null): void {
  if (state.activeSessionId === sessionId) return
  state = { ...state, activeSessionId: sessionId }
  emit()
}

/**
 * Clear the active session only if it still equals `sessionId`. Used by
 * `ChatWindow` on unmount so a stale unmount can't wipe a session another
 * window already claimed.
 */
export function clearActiveSessionId(sessionId: string): void {
  if (state.activeSessionId !== sessionId) return
  state = { ...state, activeSessionId: null }
  emit()
}

export function setIsFocused(isFocused: boolean): void {
  if (state.isFocused === isFocused) return
  state = { ...state, isFocused }
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): ActiveChatSessionState {
  return state
}

/** Server snapshot — stable reference so SSR/hydration doesn't loop. */
const SERVER_STATE: ActiveChatSessionState = {
  activeSessionId: null,
  isFocused: false,
}

function getServerSnapshot(): ActiveChatSessionState {
  return SERVER_STATE
}

/** Reactive read of the active-session state. */
export function useActiveChatSession(): ActiveChatSessionState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
